"use client";

import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { createChangeEventDetails } from "@base-ui/react/internals/createBaseUIEventDetails";
import type { BaseUIEvent } from "@base-ui/react/types";
import { createContext, useCallback, useContext, useRef } from "react";
import type * as React from "react";
import { cva, type VariantProps } from "../../lib/cva";
import { checkboxVariants } from "./checkbox";
import { CHECKBOX_FILL_CLASS, CheckboxCheckGlyph } from "./checkbox-face";
import { SWITCH_THUMB_FILL_CLASS, SWITCH_TRACK_STATE_CLASS } from "./switch";
import { Icon } from "./icon";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./tooltip";
import { useKeyboardHints } from "../../hooks/use-keyboard-hints";
import { useControllableState } from "../../hooks/use-controllable-state";
import { useIsMobile } from "../../hooks/use-media-query";
import { Drawer, DrawerInsetPanel, DrawerPopup } from "./drawer";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import { isTextEditingTarget } from "../../lib/text-editing-target";

/**
 * A text field owns the keyboard while it has focus. Base UI's typeahead and
 * list navigation run on the menu popup's keydown and `preventDefault` every
 * printable key and Home/End/arrows — including ones typed into a field
 * rendered under this menu. A portalled popover does not escape it: React
 * events bubble through the React tree, not the DOM. Base UI guards Shift+Tab
 * from a nested overlay this way itself; this closes the rest. Escape still
 * dismisses — that listener is on the document, not the popup.
 */
export function releaseKeyboardToTextField(
  event: BaseUIEvent<React.KeyboardEvent<HTMLElement>>,
): void {
  if (isTextEditingTarget(event.target)) event.preventBaseUIHandler();
}

/** What a popup may hang off instead of a trigger of its own: an element, a
 *  ref to one, a resolver, or a virtual element (an object carrying
 *  `getBoundingClientRect`). For a popup with no control of its own — a
 *  context menu placed on the row it manages, a popover over a table cell.
 *  Prefer a real trigger wherever a control exists: `render` puts the
 *  trigger's handlers on that control's own element, so a joined bar keeps
 *  its geometry, and only a registered trigger lets a second press close the
 *  menu it opened. An anchored menu with no trigger cannot host submenus:
 *  Base UI registers a menu's tree node only through its trigger, so a
 *  triggerless root reads a submenu opening as a sibling and closes itself. */
export type MenuAnchor = MenuPrimitive.Positioner.Props["anchor"];

const MenuCloseContext = createContext<(() => void) | null>(null);
const closeNothing = () => {};

/**
 * Closes the menu that holds the caller, submenus included. A menu item
 * closes its menu by itself; content that is not an item, such as a picker
 * in a submenu, calls this after a pick. Outside a menu it does nothing.
 */
export function useCloseMenu(): () => void {
  return useContext(MenuCloseContext) ?? closeNothing;
}

/** Gives the menu's content `useCloseMenu`, through the root's `actionsRef`. */
export function MenuCloseProvider({
  actionsRef,
  children,
}: {
  actionsRef: React.RefObject<MenuPrimitive.Root.Actions | null>;
  children: React.ReactNode;
}): React.ReactElement {
  const close = useCallback(() => actionsRef.current?.close(), [actionsRef]);
  return <MenuCloseContext.Provider value={close}>{children}</MenuCloseContext.Provider>;
}

export function Menu<Payload = unknown>({
  actionsRef,
  ...props
}: MenuPrimitive.Root.Props<Payload>): React.ReactElement {
  const ownActions = useRef<MenuPrimitive.Root.Actions | null>(null);
  const actions = actionsRef ?? ownActions;
  return (
    <MenuCloseProvider actionsRef={actions}>
      <MenuPrimitive.Root actionsRef={actions} {...props} />
    </MenuCloseProvider>
  );
}

/**
 * Pairs a `MenuTrigger` rendered OUTSIDE its `Menu` with that menu: the host
 * hands both the same handle. This is how a control that is one button inside
 * a joined cluster stays a real trigger — the popup keeps trigger semantics (a
 * second press closes it, focus returns to it) without a trigger wrapper
 * reshaping the cluster.
 *
 * A `Menu` with submenus NEEDS a trigger, attached or through a handle. Base
 * UI registers the root's floating-tree node through its trigger; a root with
 * none has no node, so its own submenu's opening reads as a sibling menu
 * opening and closes the root the moment a row is hovered.
 */
export const createMenuHandle: typeof MenuPrimitive.createHandle =
  MenuPrimitive.createHandle;
export type MenuHandle<Payload = unknown> = MenuPrimitive.Handle<Payload>;

export const MenuPortal: typeof MenuPrimitive.Portal = MenuPrimitive.Portal;

export function MenuTrigger({
  className,
  children,
  ...props
}: MenuPrimitive.Trigger.Props): React.ReactElement {
  return (
    <MenuPrimitive.Trigger
      className={className}
      data-slot="menu-trigger"
      {...props}
    >
      {children}
    </MenuPrimitive.Trigger>
  );
}

export const menuPopupVariants = cva(
  "max-h-(--available-height) w-full overflow-y-auto",
  {
    variants: {
      padding: {
        none: "p-0",
        "2xs": "p-1",
        sm: "p-2",
      },
    },
  },
);

/**
 * Inset of the popup's inner scroller, on the Box scale. Omit for the standard
 * `sm` (8px) — the roomy inset that reads as sectioned chrome. `2xs` (4px) is
 * the compact opt-down for a dense popup. `none` is for a popup whose content
 * owns its own edges — full-bleed rows, dividers that must run edge-to-edge (an
 * entity selector in a submenu); with it, `MenuSeparator` drops its horizontal
 * inset too.
 */
export type MenuPopupPadding = NonNullable<
  VariantProps<typeof menuPopupVariants>["padding"]
>;

/**
 * Width floor by what the menu IS, not by how long its longest label happens to
 * be. Two jobs, two rungs:
 *
 * - `action` — a short list of verbs off a row, a cell or a toolbar overflow.
 *   Compact, because it is read as a burst and dismissed. The floor only stops a
 *   two-item menu ("Edit", "Delete") reading as a fragment of a menu.
 * - `panel` — a menu anchored to a header or rail that the reader BROWSES:
 *   create menus, the workspace switcher, a column's settings. Same 280 as
 *   `SELECTOR_CONTENT_WIDTH`, so a picker and a panel opened from the same
 *   toolbar are the same object.
 * - `select` — the value list under a selector chip (a filter's "All / Active /
 *   Invited", a sort's presets): a few short words the reader picks ONE of. It
 *   holds 200 whatever the labels measure, so a chip whose values are all
 *   one short word does not open a sliver beside the chip whose values are
 *   long, and every chip in one row opens the same-sized list.
 *
 * A SUBMENU that carries a search field (an entity picker, a filter's values)
 * is the browse case by definition — it takes `panel`, never the content size:
 * a searchable list that shrink-wraps its rows reads as a sliver beside the
 * menu that opened it, and its width jumps as the query filters the rows.
 *
 * Omit for a content-sized popup (the base 128px floor). A raw number at a call
 * site is how one genre ended up holding 200, 220, 260 and 300 at once.
 *
 * Pixels, applied through `style`, not a Tailwind class: the popup's base floor
 * is `not-[class*='w-']:min-w-32`, whose `:not()` compound outweighs a plain
 * `min-w-[…]` utility, so a class here loses to 128px.
 */
export type MenuPopupWidth = "select" | "action" | "panel";

export const MENU_POPUP_WIDTH: Record<MenuPopupWidth, number> = {
  select: 200,
  action: 256,
  // Equal to SELECTOR_CONTENT_WIDTH. Not imported — that would point the
  // definition layer at a particle built on top of it — so the menu suite
  // asserts the two are equal instead.
  panel: 280,
};

/** The rung, unless the call site pinned its own number. A caller's own
 *  `style.minWidth` still wins over both. */
export function resolveMenuMinWidth(
  width: MenuPopupWidth | undefined,
  minWidth: number | string | undefined,
  style: React.CSSProperties | undefined,
): React.CSSProperties | undefined {
  const resolved = minWidth ?? (width && MENU_POPUP_WIDTH[width]);
  return resolved != null ? { minWidth: resolved, ...style } : style;
}

/** The default `finalFocus` for every MenuPopup: a keyboard close (Escape,
 *  Enter on an item) returns focus to the trigger, where the keyboard user
 *  is; a pointer close does not — Base UI's own default restores (and rings)
 *  the trigger after ANY close, which parks `:focus-visible` on a control the
 *  pointer already left, and pins every hover-revealed trigger visible
 *  through its `focus-within` reveal. The empty string is Base UI's "unknown
 *  interaction" (a programmatic close): keep the default restore there.
 *  A consumer-passed `finalFocus` still wins (props spread after it). */
const menuFinalFocus = (
  closeType: "mouse" | "touch" | "pen" | "keyboard" | "",
): boolean => closeType === "keyboard" || closeType === "";

/** The roles Base UI puts on a menu's own rows. A popup's first one is what
 *  `autoHighlight` marks. */
const MENU_ITEM_ROLES =
  '[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"]';

/** Marks the row `autoHighlight` primes. Read only by the popup's own CSS, and
 *  only while no row is really highlighted. */
const AUTO_HIGHLIGHT_ATTR = "data-auto-highlighted";

/** Primes the marked row, and stands down the moment a real highlight exists —
 *  the pointer entering the popup, or an arrow key. `:has()` on the popup's own
 *  content is what makes the two mutually exclusive without any state.
 *
 *  Spelled out rather than built from {@link AUTO_HIGHLIGHT_ATTR}: Tailwind
 *  scans source text for class names and generates nothing for a template
 *  literal. The two must be edited together. */
const AUTO_HIGHLIGHT_CLASS =
  "[&:not(:has([data-highlighted]))_[data-auto-highlighted]]:bg-wash-hover";

/**
 * Primes the popup's first row: it reads as the row a press would act on, until
 * the reader points at another.
 *
 * It is a MARK plus a CSS rule, never focus. Base UI's highlight follows FOCUS,
 * and focus is not free to take: while the pointer rests on the row that opened
 * a submenu, Base UI holds focus there and hands it back to that row on the
 * next pointer event. A menu that fights for focus wins in a test, where the
 * pointer stops moving, and loses in a hand. Marking is also what a submenu
 * needs to be honest — the reader is still pointing at the PARENT row, so
 * nothing in the submenu has really been reached yet.
 *
 * The mark hangs off a ref CALLBACK rather than an effect because the popup's
 * React component stays mounted while the menu is closed — a submenu's does for
 * the whole life of its parent — and only the DOM comes and goes.
 */
function useAutoHighlight(enabled: boolean | undefined) {
  return useCallback(
    (content: HTMLDivElement | null) => {
      if (!content || !enabled) return undefined;

      const mark = () => {
        const first = content.querySelector<HTMLElement>(MENU_ITEM_ROLES);
        for (const stale of content.querySelectorAll(
          `[${AUTO_HIGHLIGHT_ATTR}]`,
        )) {
          if (stale !== first) stale.removeAttribute(AUTO_HIGHLIGHT_ATTR);
        }
        first?.setAttribute(AUTO_HIGHLIGHT_ATTR, "");
      };

      mark();
      // Which row is first moves: rows arrive from a query after the popup
      // does, and a list under a search field re-orders as it is typed into.
      // Only child lists are watched, so the attribute writes above cannot
      // feed back in.
      const rows = new MutationObserver(mark);
      rows.observe(content, { childList: true, subtree: true });
      return () => rows.disconnect();
    },
    [enabled],
  );
}

export function MenuPopup({
  children,
  className,
  sideOffset = 4,
  align = "center",
  alignOffset,
  side = "bottom",
  anchor,
  collisionPadding,
  portalProps,
  padding = "sm",
  width,
  minWidth,
  style,
  onKeyDown,
  autoHighlight,
  ...props
}: MenuPrimitive.Popup.Props & Omittable<{
  align?: MenuPrimitive.Positioner.Props["align"];
  sideOffset?: MenuPrimitive.Positioner.Props["sideOffset"];
  alignOffset?: MenuPrimitive.Positioner.Props["alignOffset"];
  side?: MenuPrimitive.Positioner.Props["side"];
  anchor?: MenuPrimitive.Positioner.Props["anchor"];
  collisionPadding?: MenuPrimitive.Positioner.Props["collisionPadding"];
  portalProps?: MenuPrimitive.Portal.Props;
  padding?: MenuPopupPadding;
  /** Width floor by menu kind — see MenuPopupWidth. */
  width?: MenuPopupWidth;
  /** Minimum popup width, for a popup whose content sets a width no rung
   *  describes (a swatch grid). Prefer `width`. A number is pixels. */
  minWidth?: number | string;
  /** Plain style object only — the width resolver merges into it. */
  style?: React.CSSProperties;
  /** Prime the first row as the popup opens, so a pointer-opened menu reads as
   *  a list with a place in it rather than a cold block. It is a look, not a
   *  selection: the row a press WOULD act on, standing down the moment the
   *  pointer or an arrow key reaches a real row. Leave it off for a menu whose
   *  first row is a destructive or otherwise costly action. */
  autoHighlight?: boolean;
}>): React.ReactElement {
  const contentRef = useAutoHighlight(autoHighlight);

  return (
    <MenuPortal {...portalProps}>
      <MenuPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="z-50"
        collisionPadding={collisionPadding}
        data-slot="menu-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <MenuPrimitive.Popup
          finalFocus={menuFinalFocus}
          className={cn(
            "relative flex not-[class*='w-']:min-w-32 origin-(--transform-origin) rounded-lg border bg-popover not-dark:bg-clip-padding shadow-popover outline-none before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] focus:outline-none dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
            className,
          )}
          // Portalled, so it inherits no surface scope — names its own, and
          // descendant `border-border` draws the popover-tuned hairline.
          data-on="popover"
          data-slot="menu-popup"
          data-width={width}
          onKeyDown={(event) => {
            releaseKeyboardToTextField(event);
            onKeyDown?.(event);
          }}
          style={resolveMenuMinWidth(width, minWidth, style)}
          {...props}
        >
          {/* `data-padding` is what MenuSeparator reads to run edge to edge in a
              `padding="none"` popup, where sections own their own insets. */}
          <div
            className={cn(
              menuPopupVariants({ padding }),
              autoHighlight && AUTO_HIGHLIGHT_CLASS,
            )}
            data-padding={padding}
            ref={contentRef}
          >
            {children}
          </div>
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPortal>
  );
}

export function MenuGroup(
  props: MenuPrimitive.Group.Props,
): React.ReactElement {
  return <MenuPrimitive.Group data-slot="menu-group" {...props} />;
}

/**
 * The joined strip: one bordered rounded row whose segments share hairline
 * dividers, clipped so the end segments take the row's corners. `mb-2` gives
 * the row the same air below it a `MenuSeparator` would — the strip's own
 * border already draws the boundary, so a separator right under it would
 * double the line.
 */
export const MENU_ACTION_ROW_CLASS =
  "mb-2 flex items-stretch divide-x divide-border overflow-clip rounded-md border";

/**
 * A segment of the strip: an equal-width centered cell, 32px tall with a
 * quieter 12px label — a utility chip, one rung under the 14px rows below
 * it. Square corners — the row's clip supplies the outer radius. Labels
 * inherit the class's type (`Text size={null}`), never restate a size.
 */
export const MENU_ACTION_ROW_ITEM_CLASS =
  "flex min-h-8 flex-1 cursor-pointer select-none items-center justify-center gap-1.5 whitespace-nowrap px-2 py-1 text-xs text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-64 data-highlighted:bg-wash-hover data-highlighted:text-foreground [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0";

/**
 * A row of joined quick actions heading a menu — the compact strip for the
 * two-word utilities (copy, open) that would each burn a full-width row.
 * Children are `MenuActionRowItem`s (or the context-menu twin): real menu
 * items, so they highlight, keyboard-navigate and close the menu like any
 * other row. Arrow keys walk them in DOM order — the strip trades per-segment
 * arrow geometry for one-line density, so keep it to short, low-stakes verbs.
 */
export function MenuActionRow({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn(MENU_ACTION_ROW_CLASS, className)}
      data-slot="menu-action-row"
      role="group"
      {...props}
    />
  );
}

export function MenuActionRowItem({
  className,
  disabledReason,
  ...props
}: MenuPrimitive.Item.Props & MenuRowDisabledReasonProps): React.ReactElement {
  return withMenuRowDisabledReason(
    (reasonClass) => (
      <MenuPrimitive.Item
        className={cn(MENU_ACTION_ROW_ITEM_CLASS, reasonClass, className)}
        data-slot="menu-action-row-item"
        {...props}
      />
    ),
    props.disabled,
    disabledReason,
  );
}

/**
 * A menu item promoted to the primary Button treatment: the solid themed
 * fill, the 32px rung, full width, squared corners (the button spans the
 * menu region, so it takes the region's geometry, not the stadium). Still a
 * REAL menu item — it highlights, keyboard-navigates and closes the menu on
 * press. For the ONE action a menu promotes above its rows, seated at the
 * foot as its own group: a full-bleed `MenuSeparator` above it draws the
 * boundary, and the separator's own spacing is the air. The highlight wears
 * the same `bg-primary/90` the Button's hover does, so pointer and keyboard
 * read as one state.
 */
export const MENU_BUTTON_ITEM_CLASS =
  "flex h-8 w-full cursor-pointer select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary bg-primary px-3 font-medium text-primary-foreground text-sm shadow-primary/24 shadow-xs outline-none not-data-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-64 data-highlighted:bg-primary/90 active:inset-shadow-[0_1px_--theme(--color-black/8%)] active:shadow-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0";

export function MenuButtonItem({
  className,
  ...props
}: MenuPrimitive.Item.Props): React.ReactElement {
  return (
    <MenuPrimitive.Item
      className={cn(MENU_BUTTON_ITEM_CLASS, className)}
      data-slot="menu-button-item"
      {...props}
    />
  );
}

/**
 * A disabled row keeps its seat and says why. Rows the reader cannot use are
 * disabled, not hidden — a menu whose rows come and go by role makes the same
 * row a different row depending on who opened it — and the reason rides a
 * tooltip on the row. The tooltip's trigger IS the row element, by render
 * composition: no wrapper lands between the row and its children, so the
 * direct-child glyph rules (`[&>svg]`) hold and a disabled row lines up with
 * its neighbours. The Tooltip root renders no DOM and the row still registers
 * through the menu's composite-list context. Pointer events come back on the
 * disabled row so the hover (and the `not-allowed` cursor) lands; Base UI still
 * refuses the press.
 */
export type MenuRowDisabledReasonProps = Omittable<{
  /** Why the row is unavailable. Shown as a tooltip on hover while the row is
   *  `disabled`; inert otherwise, so a caller can keep it wired and let
   *  `disabled` alone decide. */
  disabledReason?: string;
}>;

const MENU_DISABLED_REASON_CLASS = "data-disabled:pointer-events-auto";

/**
 * Builds a menu row through `build`, then mounts it as the trigger of its
 * reason tooltip when the row is disabled with a reason. `build` receives the
 * class that re-arms pointer events (or `undefined` for an enabled row) to
 * fold into the row's own class list. Shared by every row kind in the menu
 * and context-menu families.
 */
export function withMenuRowDisabledReason(
  build: (reasonClass: string | undefined) => React.ReactElement,
  disabled: boolean | undefined,
  disabledReason: string | undefined,
): React.ReactElement {
  const reason = disabled && disabledReason ? disabledReason : undefined;
  const row = build(reason ? MENU_DISABLED_REASON_CLASS : undefined);
  if (!reason) return row;
  return (
    <Tooltip>
      <TooltipTrigger render={row} />
      <TooltipPopup side="top">{reason}</TooltipPopup>
    </Tooltip>
  );
}

export const menuItemVariants = cva(
  "flex min-h-7 cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-inset:ps-8 data-[variant=destructive]:not-data-disabled:text-destructive-foreground data-highlighted:text-foreground data-disabled:opacity-64 [&>svg:not([class*='opacity-'])]:opacity-80 [&>svg:not([class*='size-'])]:size-4 [&>svg]:pointer-events-none [&>svg]:-mx-0.5 [&>svg]:shrink-0",
  {
    variants: {
      size: {
        lg: "gap-2.5 min-h-8 [&>svg]:mx-0",
      },
    },
  },
);

export function MenuItem({
  className,
  inset,
  variant = "default",
  size,
  disabledReason,
  ...props
}: MenuPrimitive.Item.Props & MenuRowDisabledReasonProps & Omittable<{
  inset?: boolean;
  variant?: "default" | "destructive";
  /** `"lg"` keeps the roomy 32px row on desktop instead of the compact 28px,
   *  and lets the glyph sit at its natural inset (no optical tightening) so
   *  the label lands on a 42px edge inside an 8px-padded section. For
   *  identity/section menus; omit for the compact default. */
  size?: VariantProps<typeof menuItemVariants>["size"];
}>): React.ReactElement {
  return withMenuRowDisabledReason(
    (reasonClass) => (
      <MenuPrimitive.Item
        className={cn(menuItemVariants({ size }), reasonClass, className)}
        data-inset={inset}
        data-slot="menu-item"
        data-variant={variant}
        {...props}
      />
    ),
    props.disabled,
    disabledReason,
  );
}

export function MenuLinkItem({
  className,
  inset,
  variant = "default",
  closeOnClick = true,
  ...props
}: MenuPrimitive.LinkItem.Props & Omittable<{
  inset?: boolean;
  variant?: "default" | "destructive";
}>): React.ReactElement {
  return (
    <MenuPrimitive.LinkItem
      className={cn(
        "flex min-h-8 cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-inset:ps-8 data-[variant=destructive]:not-data-disabled:text-destructive-foreground data-highlighted:text-foreground data-disabled:opacity-64 [&>svg:not([class*='opacity-'])]:opacity-80 [&>svg:not([class*='size-'])]:size-4 [&>svg]:pointer-events-none [&>svg]:-mx-0.5 [&>svg]:shrink-0",
        className,
      )}
      closeOnClick={closeOnClick}
      data-inset={inset}
      data-slot="menu-link-item"
      data-variant={variant}
      {...props}
    />
  );
}

export const menuCheckboxItemVariants = cva(
  "min-h-8 in-data-[side=none]:min-w-[calc(var(--anchor-width)+1.25rem)] cursor-pointer items-center gap-2 rounded-sm py-1.5 ps-2 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-highlighted:text-foreground data-disabled:opacity-64 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "grid grid-cols-[.75rem_1fr] pe-4",
        switch: "grid grid-cols-[1fr_auto] gap-4 pe-1.5",
        trailing: "flex pe-2",
        checkbox: "grid grid-cols-[1fr_auto] gap-4 pe-2",
      },
    },
  },
);

export function MenuCheckboxItem({
  className,
  children,
  checked,
  variant = "default",
  disabledReason,
  ...props
}: MenuPrimitive.CheckboxItem.Props & MenuRowDisabledReasonProps & Omittable<{
  /** Where the checked state reads.
   *
   *  `"default"` reserves a leading column for the check, so a run of items
   *  keeps one label edge whether or not anything is on.
   *
   *  `"trailing"` reserves NOTHING: the content leads the row and the check
   *  sits in the trailing gutter, on the checked items only. For a list whose
   *  items already lead with a glyph or an avatar of their own — a leading
   *  column pushes that identity off the edge it shares with every other list,
   *  and stands empty on most rows to do it. It also puts the check in the
   *  same gutter a sibling row's submenu chevron uses.
   *
   *  `"checkbox"` draws the library checkbox on the trailing edge, in BOTH
   *  states. The empty box is the affordance that says the row toggles — a
   *  trailing check on the checked rows only leaves an off row looking like a
   *  plain action. For a lone yes/no flag among rows that open submenus: the
   *  box lands in the chevron gutter and reads as pressable. The row is the
   *  control; the box only draws its state.
   *
   *  `"switch"` is for a proposition to agree with rather than one of a set. */
  variant?: VariantProps<typeof menuCheckboxItemVariants>["variant"];
}>): React.ReactElement {
  return withMenuRowDisabledReason(
    (reasonClass) => (
    <MenuPrimitive.CheckboxItem
      checked={checked}
      className={cn(menuCheckboxItemVariants({ variant }), reasonClass, className)}
      data-slot="menu-checkbox-item"
      {...props}
    >
      {variant === "trailing" ? (
        <>
          {children}
          <MenuPrimitive.CheckboxItemIndicator className="ms-auto -me-0.5">
            <svg
              aria-hidden="true"
              fill="none"
              height="24"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M5.252 12.7 10.2 18.63 18.748 5.37" />
            </svg>
          </MenuPrimitive.CheckboxItemIndicator>
        </>
      ) : variant === "checkbox" ? (
        <>
          <span className="col-start-1">{children}</span>
          {/* Mounted in both states — the empty box IS the affordance. It
              wears the standalone Checkbox's face so the two cannot drift, and
              takes the row's highlight as its hover: the pointer is on the
              row, never on the box. */}
          <MenuPrimitive.CheckboxItemIndicator
            className={cn(
              checkboxVariants(),
              "in-data-highlighted:not-data-checked:border-border-strong",
            )}
            keepMounted
          >
            <span
              className={cn(
                CHECKBOX_FILL_CLASS,
                "bg-primary in-data-unchecked:hidden",
              )}
            >
              <CheckboxCheckGlyph />
            </span>
          </MenuPrimitive.CheckboxItemIndicator>
        </>
      ) : variant === "switch" ? (
        <>
          <span className="col-start-1">{children}</span>
          {/* Wears the standalone Switch's state fills so the two faces
              cannot drift. */}
          <MenuPrimitive.CheckboxItemIndicator
            className={cn(
              "inset-shadow-[0_1px_--theme(--color-black/4%)] inline-flex h-[calc(var(--thumb-size)+2px)] w-[calc(var(--thumb-size)*2-2px)] shrink-0 items-center rounded-full p-px outline-none transition-[background-color,box-shadow] duration-base [--thumb-size:--spacing(3)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background data-disabled:opacity-64",
              SWITCH_TRACK_STATE_CLASS,
            )}
            keepMounted
          >
            <span
              className={cn(
                "pointer-events-none block aspect-square h-full in-[[data-slot=menu-checkbox-item][data-checked]]:origin-[var(--thumb-size)_50%] origin-left in-[[data-slot=menu-checkbox-item][data-checked]]:translate-x-[calc(var(--thumb-size)-4px)] in-[[data-slot=menu-checkbox-item]:active]:not-data-disabled:scale-x-110 in-[[data-slot=menu-checkbox-item]:active]:rounded-[var(--thumb-size)/calc(var(--thumb-size)*1.10)] rounded-(--thumb-size) shadow-sm/5 will-change-transform [transition:translate_.15s,border-radius_.15s,scale_.1s_.1s,transform-origin_.15s,background-color_.15s]",
                SWITCH_THUMB_FILL_CLASS,
              )}
            />
          </MenuPrimitive.CheckboxItemIndicator>
        </>
      ) : (
        <>
          <MenuPrimitive.CheckboxItemIndicator className="col-start-1 -ms-0.5">
            <svg
              aria-hidden="true"
              fill="none"
              height="24"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M5.252 12.7 10.2 18.63 18.748 5.37" />
            </svg>
          </MenuPrimitive.CheckboxItemIndicator>
          <span className="col-start-2">{children}</span>
        </>
      )}
    </MenuPrimitive.CheckboxItem>
    ),
    props.disabled,
    disabledReason,
  );
}

export function MenuRadioGroup(
  props: MenuPrimitive.RadioGroup.Props,
): React.ReactElement {
  return <MenuPrimitive.RadioGroup data-slot="menu-radio-group" {...props} />;
}

export function MenuRadioItem({
  className,
  children,
  meta,
  disabledReason,
  ...props
}: MenuPrimitive.RadioItem.Props & MenuRowDisabledReasonProps & Omittable<{
  /** A persistent fact at the row's trailing edge — a count of what the
   *  option would yield, a unit. Quiet ink on the caption rung with tabular
   *  digits, so a column of them lines up down the list and never outweighs
   *  the labels. Always shown, never hover-gated: a fact revealed on hover is
   *  a fact the row failed to state. */
  meta?: React.ReactNode;
}>): React.ReactElement {
  return withMenuRowDisabledReason(
    (reasonClass) => (
    <MenuPrimitive.RadioItem
      className={cn(
        "grid min-h-8 in-data-[side=none]:min-w-[calc(var(--anchor-width)+1.25rem)] cursor-pointer grid-cols-[.75rem_1fr] items-center gap-2 rounded-sm py-1.5 ps-2 pe-4 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-highlighted:text-foreground data-disabled:opacity-64 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        reasonClass,
        className,
      )}
      data-slot="menu-radio-item"
      {...props}
    >
      <MenuPrimitive.RadioItemIndicator className="col-start-1 -ms-0.5">
        <svg
          aria-hidden="true"
          fill="none"
          height="24"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5.252 12.7 10.2 18.63 18.748 5.37" />
        </svg>
      </MenuPrimitive.RadioItemIndicator>
      <span className="col-start-2 flex min-w-0 items-center gap-3">
        {children}
        {meta != null && (
          <span
            className="ms-auto shrink-0 text-muted-foreground text-xs tabular-nums"
            data-slot="menu-item-meta"
          >
            {meta}
          </span>
        )}
      </span>
    </MenuPrimitive.RadioItem>
    ),
    props.disabled,
    disabledReason,
  );
}

export function MenuGroupLabel({
  className,
  inset,
  ...props
}: MenuPrimitive.GroupLabel.Props & Omittable<{
  inset?: boolean;
}>): React.ReactElement {
  return (
    <MenuPrimitive.GroupLabel
      className={cn(
        "mb-1.5 px-2 py-1.5 font-medium text-muted-foreground text-xs data-inset:ps-8",
        className,
      )}
      data-inset={inset}
      data-slot="menu-label"
      {...props}
    />
  );
}

export function MenuSeparator({
  className,
  ...props
}: MenuPrimitive.Separator.Props): React.ReactElement {
  return (
    <MenuPrimitive.Separator
      // Full-bleed: the rule reaches the popup edge whatever the content
      // padding is, by cancelling that padding with a negative margin per
      // rung (none = already flush, 2xs = 4px, sm = 8px). In a
      // `padding="none"` popup the sections own ALL their insets, so the
      // separator contributes no vertical air of its own either — otherwise
      // the section beside a separator reads taller than the one at the
      // popup edge.
      className={cn(
        "my-2 h-px bg-border in-data-[padding=none]:mx-0 in-data-[padding=none]:my-0 in-data-[padding=2xs]:-mx-1 in-data-[padding=sm]:-mx-2",
        className,
      )}
      data-slot="menu-separator"
      {...props}
    />
  );
}

/** The row's key binding. Nothing on a device with no keyboard to press it. */
export function MenuShortcut({
  className,
  ...props
}: React.ComponentProps<"kbd">): React.ReactElement | null {
  if (!useKeyboardHints()) return null;
  return (
    <kbd
      className={cn(
        "ms-auto font-medium font-sans text-muted-foreground/72 text-xs tracking-widest",
        className,
      )}
      data-slot="menu-shortcut"
      {...props}
    />
  );
}

/**
 * Trailing secondary text on a row that is NOT a key binding — a version, a
 * device name, a count. It keeps `MenuShortcut`'s seat and ink without its
 * keycap tracking, and it stays on devices where the shortcuts stand down.
 */
export function MenuMeta({
  className,
  ...props
}: React.ComponentProps<"span">): React.ReactElement {
  return (
    <span
      className={cn(
        "ms-auto truncate font-sans text-muted-foreground/72 text-xs",
        className,
      )}
      data-slot="menu-meta"
      {...props}
    />
  );
}

/**
 * A quiet fact under a menu's rows — a count, a consequence of the pick above
 * it — that nobody can press. It stands on the radio row's own grid, so its
 * glyph shares the check column and its text the label edge, and it reads on
 * the caption rung in muted ink. Not a row: no highlight, no focus, no role
 * in the menu's list.
 */
export function MenuNote({
  className,
  icon,
  children,
  ...props
}: React.ComponentProps<"div"> & Omittable<{
  /** The glyph in the check column, sized like the rows' (16px). */
  icon?: React.ReactNode;
}>): React.ReactElement {
  return (
    <div
      className={cn(
        "grid min-h-8 grid-cols-[.75rem_1fr] items-center gap-2 py-1.5 ps-2 pe-4 text-muted-foreground text-xs [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
        className,
      )}
      data-slot="menu-note"
      role="note"
      {...props}
    >
      <span className="col-start-1 -ms-0.5 flex items-center">{icon}</span>
      <span className="col-start-2 min-w-0">{children}</span>
    </div>
  );
}

// A submenu on a phone presents as a sheet stacked over its menu (the
// library Drawer); its trigger and popup read this to pick their sheet twin.
type MenuSubSheetValue = {
  open: boolean;
  setOpen: (open: boolean, details: MenuPrimitive.SubmenuRoot.ChangeEventDetails) => void;
};
const MenuSubSheetContext = createContext<MenuSubSheetValue | null>(null);

export type MenuSubProps = MenuPrimitive.SubmenuRoot.Props &
  Omittable<{
    /** Present the submenu as a bottom sheet on a phone, stacked over the
     *  menu. A side popup has no place beside a phone-width menu, and one
     *  that holds a searchable list has nowhere to go when its search takes
     *  the keyboard; a sheet's search lifts it to the top under the keys.
     *  Default true; `false` keeps the side popup everywhere. */
    responsive?: boolean;
  }>;

export function MenuSub({
  responsive = true,
  open: openProp,
  defaultOpen,
  onOpenChange,
  children,
  ...props
}: MenuSubProps): React.ReactElement {
  const isMobile = useIsMobile();
  const sheet = responsive && isMobile;
  // Held here for the sheet: a submenu root is not mounted then, and the
  // trigger (a plain item) and the popup (a drawer) share it.
  const [open, setOpenState] = useControllableState<boolean>({
    prop: sheet ? openProp : undefined,
    defaultProp: defaultOpen ?? false,
  });
  const setOpen = useCallback(
    (next: boolean, details: MenuPrimitive.SubmenuRoot.ChangeEventDetails) => {
      setOpenState(next);
      onOpenChange?.(next, details);
    },
    [onOpenChange, setOpenState],
  );
  if (sheet) {
    return (
      <MenuSubSheetContext.Provider value={{ open, setOpen }}>
        {children}
      </MenuSubSheetContext.Provider>
    );
  }
  return (
    <MenuPrimitive.SubmenuRoot
      data-slot="menu-sub"
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      open={openProp}
      {...props}
    >
      {children}
    </MenuPrimitive.SubmenuRoot>
  );
}

export const menuSubTriggerVariants = cva(
  "flex min-h-7 cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-popup-open:bg-wash-hover data-inset:ps-8 data-highlighted:text-foreground data-popup-open:text-foreground data-disabled:opacity-64 [&>svg:not(:last-child)]:-mx-0.5 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
  {
    variants: {
      size: {
        lg: "gap-2.5 min-h-8 [&>svg:not(:last-child)]:mx-0",
      },
    },
  },
);

export function MenuSubTrigger({
  className,
  inset,
  size,
  disabledReason,
  children,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props & MenuRowDisabledReasonProps & Omittable<{
  inset?: boolean;
  /** `"lg"` keeps the roomy 32px row on desktop instead of the compact 28px,
   *  matching `MenuItem size="lg"` so mixed rows stay uniform. For
   *  identity/section menus; omit for the compact default. */
  size?: VariantProps<typeof menuSubTriggerVariants>["size"];
}>): React.ReactElement {
  const sheetSub = useContext(MenuSubSheetContext);
  return withMenuRowDisabledReason(
    (reasonClass) =>
      sheetSub ? (
        // The sheet's door: a plain row of the menu (it highlights and
        // keyboard-navigates like any other) that opens the sheet and keeps
        // the menu — the sheet stands over it, and a pick inside closes both
        // through `useCloseMenu`.
        <MenuPrimitive.Item
          className={cn(menuSubTriggerVariants({ size }), reasonClass, className)}
          closeOnClick={false}
          data-inset={inset}
          data-slot="menu-sub-trigger"
          {...(props as MenuPrimitive.Item.Props)}
          onClick={(event) => {
            (props as MenuPrimitive.Item.Props).onClick?.(event);
            if (event.baseUIHandlerPrevented) return;
            sheetSub.setOpen(
              true,
              createChangeEventDetails<"trigger-press", { preventUnmountOnClose(): void }>(
                "trigger-press",
                event.nativeEvent,
                event.currentTarget,
                { preventUnmountOnClose() {} },
              ),
            );
          }}
        >
          {children}
          <Icon className="ms-auto -me-0.5 opacity-80" name="chevron-right" />
        </MenuPrimitive.Item>
      ) : (
        <MenuPrimitive.SubmenuTrigger
          className={cn(menuSubTriggerVariants({ size }), reasonClass, className)}
          data-inset={inset}
          data-slot="menu-sub-trigger"
          {...props}
        >
          {children}
          <Icon className="ms-auto -me-0.5 opacity-80" name="chevron-right" />
        </MenuPrimitive.SubmenuTrigger>
      ),
    props.disabled,
    disabledReason,
  );
}

export function MenuSubPopup({
  className,
  sideOffset = 0,
  alignOffset,
  align = "start",
  sheetTitle,
  ...props
}: MenuPrimitive.Popup.Props & Omittable<{
  align?: MenuPrimitive.Positioner.Props["align"];
  sideOffset?: MenuPrimitive.Positioner.Props["sideOffset"];
  alignOffset?: MenuPrimitive.Positioner.Props["alignOffset"];
  padding?: MenuPopupPadding;
  /** Width floor by menu kind, matching `MenuPopup`. */
  width?: MenuPopupWidth;
  /** See `MenuPopup`. A submenu opened on hover is the usual place for it:
   *  the pointer chose the ROW, not yet a value in it. */
  autoHighlight?: boolean;
  /** Phone sheet only: a small centred caption naming what the sheet lists —
   *  the trigger row's own label ("Assignee"), which the stacked sheet has
   *  just covered. The side popup shows nothing. See `DrawerInsetPanel`. */
  sheetTitle?: React.ReactNode;
}>): React.ReactElement {
  const defaultAlignOffset = align !== "center" ? -5 : undefined;
  const sheetSub = useContext(MenuSubSheetContext);

  // The sheet: the Drawer's own popup, its inset panel the one scroller.
  // What describes the side popup's shape — align, offsets, width, padding,
  // className — is left to it. A field tapped inside lifts the half sheet to
  // the top stop, the drawer's own rule.
  if (sheetSub) {
    const { children } = props;
    return (
      <Drawer
        onOpenChange={(next, details) =>
          sheetSub.setOpen(
            next,
            details as unknown as MenuPrimitive.SubmenuRoot.ChangeEventDetails,
          )
        }
        open={sheetSub.open}
      >
        <DrawerPopup data-slot="menu-sub-sheet" showBar>
          <DrawerInsetPanel title={sheetTitle}>{children}</DrawerInsetPanel>
        </DrawerPopup>
      </Drawer>
    );
  }

  return (
    <MenuPopup
      align={align}
      alignOffset={alignOffset ?? defaultAlignOffset}
      className={className}
      data-slot="menu-sub-content"
      side="inline-end"
      sideOffset={sideOffset}
      {...props}
    />
  );
}
